import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface DocumentBlobPreviewProps {
  blobUrl: string;
  blob?: Blob | null;
  mimeType: string;
  title: string;
  fileName?: string;
  pdfFragment?: string;
}

interface TextContentPreviewProps {
  blobUrl: string;
  blob?: Blob | null;
  mode: 'markdown' | 'text';
}

const MARKDOWN_FILE_RE = /\.(md|markdown)$/i;

function isMarkdownDocument(mimeType: string, fileName?: string): boolean {
  const normalizedMimeType = mimeType.toLowerCase();
  const normalizedFileName = (fileName ?? '').trim();

  return normalizedMimeType.includes('markdown') || MARKDOWN_FILE_RE.test(normalizedFileName);
}

function TextContentPreview({ blobUrl, blob, mode }: TextContentPreviewProps) {
  const [text, setText] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setText(null);
    setLoadError(null);

    void (async () => {
      try {
        const value = blob
          ? await blob.text()
          : await fetch(blobUrl).then((response) => response.text());

        if (!cancelled) {
          setText(value);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Textinhalt konnte nicht geladen werden.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blob, blobUrl]);

  if (loadError) {
    return (
      <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
        {loadError}
      </div>
    );
  }

  if (text === null) {
    return (
      <div className="absolute inset-0 flex items-center justify-center px-6 text-sm text-muted-foreground">
        Inhalt wird aufbereitet...
      </div>
    );
  }

  if (mode === 'markdown') {
    return (
      <div data-testid="document-markdown-preview" className="absolute inset-0 overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl rounded-xl border border-border/70 bg-background/90 p-5 shadow-sm backdrop-blur-sm sm:p-6">
          <div className="max-w-none text-sm text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline underline-offset-4"
                  >
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="my-3 border-l-2 border-primary/30 pl-4 italic text-muted-foreground">
                    {children}
                  </blockquote>
                ),
                code: ({ children, className }) => {
                  if (className) {
                    return <code className={`${className} text-xs`}>{children}</code>;
                  }

                  return (
                    <code className="rounded bg-muted/70 px-1 py-0.5 font-mono text-[11px] text-foreground">
                      {children}
                    </code>
                  );
                },
                h1: ({ children }) => (
                  <h1 className="mb-3 mt-5 text-xl font-semibold tracking-tight text-foreground first:mt-0">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="mb-2 mt-5 text-lg font-semibold tracking-tight text-foreground first:mt-0">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="mb-2 mt-4 text-base font-semibold text-foreground first:mt-0">
                    {children}
                  </h3>
                ),
                hr: () => <hr className="my-5 border-border/70" />,
                img: ({ src, alt, title }) => (
                  <span className="my-3 block">
                    <img
                      src={src ?? ''}
                      alt={alt ?? ''}
                      title={title ?? alt ?? ''}
                      className="max-h-[32rem] max-w-full rounded-lg border border-border shadow-sm"
                    />
                  </span>
                ),
                li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                ol: ({ children }) => <ol className="my-3 list-decimal space-y-1 pl-5">{children}</ol>,
                p: ({ children }) => <p className="my-3 leading-7 text-foreground/90">{children}</p>,
                pre: ({ children }) => (
                  <pre className="my-3 overflow-x-auto rounded-lg border border-border bg-muted/40 p-4 text-xs leading-relaxed">
                    {children}
                  </pre>
                ),
                table: ({ children }) => (
                  <div className="my-4 overflow-x-auto rounded-lg border border-border">
                    <table className="min-w-full border-collapse text-xs">{children}</table>
                  </div>
                ),
                tbody: ({ children }) => <tbody className="divide-y divide-border/60">{children}</tbody>,
                td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
                th: ({ children }) => (
                  <th className="bg-muted/50 px-3 py-2 text-left font-semibold text-foreground">{children}</th>
                ),
                ul: ({ children }) => <ul className="my-3 list-disc space-y-1 pl-5">{children}</ul>,
              }}
            >
              {text}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  return (
    <pre
      data-testid="document-text-preview"
      className="absolute inset-0 overflow-auto p-6 font-mono text-[13px] leading-6 text-foreground/80 whitespace-pre-wrap"
    >
      {text}
    </pre>
  );
}

export function DocumentBlobPreview({ blobUrl, blob, mimeType, title, fileName, pdfFragment }: DocumentBlobPreviewProps) {
  const normalizedMimeType = mimeType.toLowerCase();
  const isPdf = normalizedMimeType.includes('pdf');
  const isHtml = normalizedMimeType.includes('html');
  const isImage = normalizedMimeType.startsWith('image/');
  const isText = normalizedMimeType.startsWith('text/') && !isHtml;
  const isMarkdown = isMarkdownDocument(normalizedMimeType, fileName);
  const isTextLike = isText || isMarkdown;
  const canPreview = isPdf || isHtml || isImage || isTextLike;

  if (isPdf || isHtml) {
    const src = isPdf && pdfFragment ? `${blobUrl}${pdfFragment}` : blobUrl;

    return (
      <iframe
        key={src}
        src={src}
        className="absolute inset-0 h-full w-full border-0"
        title={title}
      />
    );
  }

  if (isImage) {
    return (
      <div className="absolute inset-0 flex items-center justify-center overflow-auto p-6">
        <img src={blobUrl} alt={title} className="max-h-full max-w-full rounded object-contain" />
      </div>
    );
  }

  if (isTextLike) {
    return <TextContentPreview blobUrl={blobUrl} blob={blob} mode={isMarkdown ? 'markdown' : 'text'} />;
  }

  if (!canPreview) {
    return (
      <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
        Vorschau nicht verfügbar für diesen Dateityp ({mimeType}).
      </div>
    );
  }

  return null;
}